import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Title, Text, Button, Table, Group, Card, Badge, LoadingOverlay, Image, Modal } from '@mantine/core';
import { productsUrl } from '../../api';
import { toast } from 'react-toastify';

const DropDetails = () => {
    const { id } = useParams();
    const [drop, setDrop] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        console.log("DropDetails Component Mounted - Version 2");
        const fetchData = async () => {
            try {
                // Fetch Drop
                const dropRes = await fetch(`https://backend.cdp3d.com/api/drops/${id}`);
                const dropData = await dropRes.json();
                setDrop(dropData);

                // Fetch Products
                const prodRes = await fetch(`${productsUrl}?dropId=${id}`); 
                const prodData = await prodRes.json();
                setProducts(prodData.data || []); 
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    const [opened, setOpened] = useState(false);
    const [allProducts, setAllProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);

    const fetchAllProducts = async () => {
        const res = await fetch(productsUrl);
        const data = await res.json();
        setAllProducts(data.data || []);
    };

    const handleUpdateDropProducts = async () => {
        try {
            setLoading(true);
            // We need to send ALL product IDs that should be in the drop
            // This includes existing ones + new selected ones (if we are treating selection as 'add')
            // Or we can treat the selection as the 'final state'.
            // Let's treat the modal as 'Select products for this drop', so it should pre-select existing ones.

            await fetch(`https://backend.cdp3d.com/api/drops/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ products: selectedProducts })
            });

            toast.success('Drop products updated!');
            setOpened(false);
            window.location.reload();
        } catch (err) {
            toast.error('Failed to update drop products');
        } finally {
            setLoading(false);
        }
    };

    const openManageModal = () => {
        // Pre-select existing products
        setSelectedProducts(products.map(p => p._id));
        fetchAllProducts();
        setOpened(true);
    };

    const toggleProductSelection = (prodId) => {
        if (selectedProducts.includes(prodId)) {
            setSelectedProducts(prev => prev.filter(id => id !== prodId));
        } else {
            setSelectedProducts(prev => [...prev, prodId]);
        }
    };

    if (loading) return <LoadingOverlay visible />;
    if (!drop) return <Text>Drop not found</Text>;

    return (
        <div style={{ padding: 20 }}>
            {/* Header */}
            <Group position="apart" mb="lg">
                <div>
                    <Title order={2}>{drop.name}</Title>
                    <Text color="dimmed">{drop.description}</Text>
                </div>
                <Badge size="lg" color={drop.status === 'active' ? 'green' : 'blue'}>{drop.status}</Badge>
            </Group>

            {/* Banner */}
            {drop.bannerImage && (
                <Card mb="xl" padding="0" radius="md" withBorder>
                     <Image src={`https://backend.cdp3d.com${drop.bannerImage}`} height={200} fit="cover" />
                </Card>
            )}

            {/* Products Section */}
            <Group position="apart" mb="md">
                <Title order={3}>Products in this Drop</Title>
                <Group>
                    <Group>
                        <Button variant="filled" color="grape" onClick={openManageModal}>Link/Unlink Products</Button>
                        <Button component={Link} to={`/add-product?dropId=${id}`}>Create New Product</Button>
                    </Group>
                </Group>
            </Group>

            <Table>
                <thead>
                    <tr>
                        <th>Image</th>
                        <th>Name</th>
                        <th>Price</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {products.length === 0 ? (
                        <tr><td colSpan={4}><Text align="center">No products in this drop yet.</Text></td></tr>
                    ) : (
                        products.map(product => (
                            <tr key={product._id}>
                                <td>
                                    <Image src={product.coverImage?.startsWith('/') ? `https://backend.cdp3d.com${product.coverImage}` : product.coverImage} width={50} height={50} radius="sm" />
                                </td>
                                <td>{product.name || product.title}</td>
                                <td>{product.currentPrice}</td>
                                <td>
                                    <Button component={Link} to={`/product/${product._id}`} variant="subtle" size="xs">View</Button>
                                    <Button component={Link} to={`/edit-product/${product._id}`} variant="subtle" size="xs" color="blue">Edit</Button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>

            {/* Manage Products Modal */}
            <Modal opened={opened} onClose={() => setOpened(false)} title="Manage Drop Products" size="lg">
                <Text size="sm" color="dimmed" mb="md">Select products to include in this drop.</Text>
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    <Table>
                        <thead>
                            <tr>
                                <th>Select</th>
                                <th>Name</th>
                                <th>Current Drop</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allProducts.map(p => (
                                <tr key={p._id} style={{ opacity: (p.dropId && p.dropId !== id) ? 0.5 : 1 }}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedProducts.includes(p._id)}
                                            onChange={() => toggleProductSelection(p._id)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </td>
                                    <td>{p.title}</td>
                                    <td>
                                        {p.dropId ? (p.dropId === id ? <Badge color="green">Current</Badge> : <Badge color="gray">Other Drop</Badge>) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
                <Button fullWidth mt="md" onClick={handleUpdateDropProducts}>Save Changes</Button>
            </Modal>
        </div>
    );
};

export default DropDetails;
